import { loadStripe } from '@stripe/stripe-js';
import { Subscription } from '../components/subscription/SubscriptionStatus';
import { Transaction } from '../components/subscription/BillingHistory';
import axios from 'axios';

// API base URL - Uses environment variable with fallback to ngrok URL
const API_URL = process.env.REACT_APP_API_URL || 'https://76dd22d59457.ngrok-free.app/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true' // This helps with ngrok browser warnings
  },
});

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log('Request:', {
      url: config.url,
      method: config.method,
      headers: config.headers,
      data: config.data,
    });
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for logging and error handling
api.interceptors.response.use(
  (response) => {
    console.log('Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data,
    });
    return response;
  },
  (error) => {
    const errorDetails = {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message,
    };
    
    console.error('API Error:', JSON.stringify(errorDetails, null, 2));
    
    // Handle specific error codes
    if (error.response?.status === 511) {
      console.error('Network Authentication Required - Please check your ngrok connection');
    }
    
    return Promise.reject(error);
  }
);

// Replace with your actual Stripe publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_TYooMQauvdEDq54NiTphI7jx');

// Helper function to convert Stripe subscription to our app's format
const formatSubscription = (stripeSubscription: any): Subscription => {
  return {
    id: stripeSubscription.id,
    planId: stripeSubscription.items.data[0].price.id,
    planName: stripeSubscription.items.data[0].price.nickname || 'Subscription',
    status: stripeSubscription.status,
    currentPeriodStart: new Date(stripeSubscription.current_period_start * 1000),
    currentPeriodEnd: new Date(stripeSubscription.current_period_end * 1000),
    cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
    trialEnd: stripeSubscription.trial_end ? new Date(stripeSubscription.trial_end * 1000) : undefined
  };
};

// Helper function to convert Stripe invoice to our app's transaction format
const formatInvoice = (invoice: any): Transaction => {
  return {
    id: invoice.id,
    date: new Date(invoice.created * 1000),
    amount: invoice.amount_paid / 100, // Convert from cents to dollars
    description: invoice.lines.data[0]?.description || 'Subscription payment',
    status: invoice.status,
    receiptUrl: invoice.hosted_invoice_url
  };
};

/**
 * Get Stripe instance
 */
export const getStripe = () => stripePromise;

/**
 * Create or get a Stripe customer for the user
 * @param userId User ID
 * @param email User email
 * @param name User name
 * @returns Customer ID
 */
export const getOrCreateCustomer = async (userId: string, email: string, name: string): Promise<string> => {
  try {
    // Try to get existing customer
    const response = await api.get(`/stripe/customers/${userId}`);
    if (response.data?.customer?.id) {
      return response.data.customer.id;
    }
    throw new Error('Customer not found in response');
  } catch (error: any) {
    // If customer doesn't exist or any other error, create a new one
    if (error.response?.status === 404 || error.message === 'Customer not found' || error.message === 'Customer not found in response') {
      console.log('Creating new customer with:', { userId, email, name });
      const createResponse = await api.post('/stripe/customers', {
        email,
        name: name || userId, // Fallback to userId if name is not provided
        userId
      });
      
      if (createResponse.data?.customer?.id) {
        return createResponse.data.customer.id;
      }
      throw new Error('Failed to create customer: Invalid response from server');
    }
    
    console.error('Error in getOrCreateCustomer:', error);
    throw new Error(`Failed to get or create customer: ${error.message}`);
  }
};

/**
 * Get current subscription
 * @param userId User ID
 * @returns Subscription object or null if no active subscription
 */
export const getCurrentSubscription = async (userId: string): Promise<Subscription | null> => {
  try {
    // Get customer ID
    const customerResponse = await api.get(`/stripe/customers/${userId}`);
    const customerId = customerResponse.data?.customer?.id;
    
    if (!customerId) {
      console.log('No customer found for user:', userId);
      return null;
    }
    
    // Get subscriptions
    const subscriptionsResponse = await api.get(`/stripe/customers/${customerId}/subscriptions`);
    const subscriptions = subscriptionsResponse.data?.subscriptions || [];
    
    // Find active or trialing subscription
    const activeSubscription = subscriptions.find((sub: any) => 
      ['active', 'trialing', 'past_due', 'incomplete', 'incomplete_expired', 'unpaid'].includes(sub.status)
    );
    
    if (!activeSubscription) {
      console.log('No active subscription found for customer:', customerId);
      return null;
    }
    
    return formatSubscription(activeSubscription);
  } catch (error) {
    console.error('Error getting subscription:', error);
    // Check if it's an Axios error with a response
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Create a new subscription
 * @param userId User ID
 * @param planId Plan ID
 * @param paymentMethodId Payment method ID from Stripe
 * @returns Subscription object
 */
export const createSubscription = async (
  userId: string,
  planId: string,
  paymentMethodId: string
): Promise<Subscription> => {
  try {
    // Get customer
    const customerResponse = await api.get(`/stripe/customers/${userId}`);
    const customerId = customerResponse.data.customer.id;
    
    // Create subscription
    const subscriptionResponse = await api.post('/stripe/subscriptions', {
      customerId,
      priceId: planId,
      paymentMethodId
    });
    
    return formatSubscription(subscriptionResponse.data.subscription);
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

/**
 * Cancel a subscription
 * @param subscriptionId Subscription ID
 * @param cancelImmediately Whether to cancel immediately or at the end of the period
 * @returns Updated subscription object
 */
export const cancelSubscription = async (
  subscriptionId: string,
  cancelImmediately: boolean = false
): Promise<Subscription> => {
  try {
    const response = await api.post(`/stripe/subscriptions/${subscriptionId}/cancel`, {
      cancelImmediately
    });
    
    return formatSubscription(response.data.subscription);
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

/**
 * Get billing history for a user
 * @param userId User ID
 * @returns Array of transactions
 */
export const getBillingHistory = async (userId: string): Promise<Transaction[]> => {
  try {
    // Get customer
    const customerResponse = await api.get(`/stripe/customers/${userId}`);
    const customerId = customerResponse.data?.customer?.id;
    
    if (!customerId) {
      console.log('No customer found for user:', userId);
      return [];
    }
    
    // Get invoices
    const invoicesResponse = await api.get(`/stripe/customers/${customerId}/invoices`);
    const invoices = invoicesResponse.data?.invoices || [];
    
    return invoices.map(formatInvoice);
  } catch (error) {
    console.error('Error getting billing history:', error);
    // If customer not found or no invoices, return empty array
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

/**
 * Start a free trial
 * @param userId User ID
 * @param email User email
 * @returns Trial subscription object
 */
export const startFreeTrial = async (userId: string, email: string): Promise<Subscription> => {
  try {
    // Get or create customer
    const customerId = await getOrCreateCustomer(userId, email, userId);
    
    // Create trial subscription
    const response = await api.post('/stripe/trials', {
      customerId,
      priceId: 'price_1S82AyBX5zvFrqexYopDb9Ro' // Use the correct price ID
    });
    
    return formatSubscription(response.data.subscription);
  } catch (error) {
    console.error('Error starting trial:', error);
    throw error;
  }
};

/**
 * Create a checkout session for subscription
 * @param userId User ID
 * @param priceId Price ID
 * @param successUrl Success URL
 * @param cancelUrl Cancel URL
 * @param user The authenticated user object
 * @returns Session ID
 */
export const createCheckoutSession = async (
  userId: string,
  priceId: string,
  successUrl: string,
  cancelUrl: string,
  user: { email: string; fullName: string }
): Promise<string> => {
  try {
    console.log('Creating checkout session with params:', { userId, priceId, successUrl, cancelUrl });
    
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    let customerId: string;
    
    try {
      // Try to get existing customer
      const customerResponse = await api.get(`/stripe/customers/${userId}`);
      if (customerResponse.data?.customer?.id) {
        customerId = customerResponse.data.customer.id;
        console.log('Found existing customer:', customerId);
      } else {
        throw new Error('Customer not found');
      }
    } catch (error) {
      console.log('Customer not found, creating a new one...');
      // Create new customer if not found
      const customerResponse = await api.post('/stripe/customers', {
        email: user.email,
        name: user.fullName || 'Customer',
        userId: userId
      });
      
      if (!customerResponse.data?.customer?.id) {
        throw new Error('Failed to create customer');
      }
      
      customerId = customerResponse.data.customer.id;
      console.log('Created new customer:', customerId);
    }
    
    // Create checkout session with the customer ID
    const response = await api.post('/stripe/checkout-sessions', {
      customerId,
      priceId,
      successUrl,
      cancelUrl
    });
    
    if (!response.data?.sessionId) {
      console.error('Invalid checkout session response:', response.data);
      throw new Error('Failed to create checkout session');
    }
    
    console.log('Checkout session created successfully:', response.data.sessionId);
    return response.data.sessionId;
  } catch (error: any) {
    console.error('Error in createCheckoutSession:', error);
    
    if (error.response) {
      console.error('Server error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
      throw new Error(error.response.data?.error || error.message);
    } else if (error.request) {
      console.error('No response received from server');
      throw new Error('No response from server. Please check your connection.');
    } else {
      console.error('Request setup error:', error.message);
      throw new Error(`Request error: ${error.message}`);
    }
  }
};

/**
 * Create a customer portal session
 * @param userId User ID
 * @param returnUrl Return URL
 * @returns Portal URL
 */
export const createCustomerPortalSession = async (
  userId: string,
  returnUrl: string
): Promise<string> => {
  try {
    // Get customer
    const response = await api.get(`/stripe/customers/${userId}`);
    
    // Check if we got a valid response with customer data
    if (!response.data || !response.data.customer) {
      console.error('Invalid customer data received:', response.data);
      const error = new Error('Invalid customer data received from server');
      // @ts-ignore - Add response property to error
      error.response = { status: 404 };
      throw error;
    }
    
    const customer = response.data.customer;
    
    if (!customer.id) {
      console.error('Customer ID is missing in the response:', customer);
      const error = new Error('Customer ID is missing');
      // @ts-ignore - Add response property to error
      error.response = { status: 404 };
      throw error;
    }
    
    // Create portal session
    const portalResponse = await api.post('/stripe/customer-portal', {
      customerId: customer.id,
      returnUrl
    });
    
    if (!portalResponse.data || !portalResponse.data.url) {
      const error = new Error('Invalid response from customer portal creation');
      // @ts-ignore - Add response property to error
      error.response = { status: 500 };
      throw error;
    }
    
    return portalResponse.data.url;
  } catch (error: any) {
    console.error('Error in createCustomerPortalSession:', error);
    
    // If it's an Axios error, it will have response property
    if (error.response) {
      throw error;
    }
    
    // For other errors, wrap them in an object that has a response property
    const wrappedError = new Error(error.message || 'Failed to create customer portal session');
    // @ts-ignore - Add response property to error
    wrappedError.response = { status: 500 };
    throw wrappedError;
  }
};
